<template>
  <RtCard
    :icon="RT_CONFIG_SECTION_META.rapidTrigger.icon"
    :title="t('rt.tabs.rapidTrigger')"
    :loading="loading"
  >
    <template #header-action>
      <div class="flex items-center gap-3 shrink-0">
        <RtWarningTipsButton
          v-model:open="isRtRiskTooltipOpen"
          side="left"
          :content="RT_TRAVEL_RISK_TIP"
        />
        <SwitchLabel
          v-model="switchValue"
          :label="t('rt.rapidTrigger.enable')"
          :disabled="loading"
          class="shrink-0 whitespace-nowrap"
        />

        <RtModeSelector
          :model-value="mode"
          :disabled="loading"
          @update:model-value="(v) => emit('update:mode', v)"
        />
      </div>
    </template>
    <div class="w-full h-full flex flex-col justify-center gap-3 min-w-0">
      <div class="w-full flex items-start gap-3">
        <div class="flex-1 relative">
          <Slider
            :model-value="currentValue === undefined ? [] : [currentValue]"
            :min="sliderMinLevel"
            :max="maxLevel"
            :step="stepLevel"
            :disabled="loading || !isSwitchOn"
            thumb-alignment="contain"
            range-alignment="thumb-end"
            :thumb-size="THUMB_WIDTH"
            animated
            class="h-6 w-full relative"
            track-class="bg-glass30 h-6"
            range-class="bg-fluo-default"
            thumb-class="bg-transparent rounded-none"
            @update:model-value="onDrag"
            @value-commit="onCommit"
          >
            <template #thumb>
              <div class="relative flex items-center justify-center w-full h-full">
                <Icon icon="Direction-FIll-SwipeLR1" class="size-7" />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] -top-1 -translate-x-1/2"
                  :class="'h-4'"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] -bottom-1 -translate-x-1/2"
                  :class="'h-4'"
                />
              </div>
            </template>
          </Slider>
          <!-- 刻度：两端用「短 / 长」表示行程方向，中间档位保留竖线。 -->
          <div class="relative mt-2 h-4">
            <button
              v-for="tick in tickMarks"
              :key="tick.value"
              type="button"
              :disabled="loading || !isSwitchOn"
              class="absolute inset-y-0 w-4 -translate-x-1/2 flex items-center justify-center select-none"
              :class="isSwitchOn ? 'cursor-pointer' : 'cursor-not-allowed'"
              :style="{
                left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
              }"
              :aria-label="t('rt.rapidTrigger.setValue', { value: tick.value })"
              @click="jumpToTick(tick.value)"
            >
              <span
                v-if="tick.value === minLevel || tick.value === maxLevel"
                class="text-s-medium whitespace-nowrap transition-colors"
                :class="
                  isSwitchOn && currentValue === tick.value
                    ? 'text-brand-blue1'
                    : isSwitchOn
                      ? 'text-text-glass-default'
                      : 'text-text-disabled'
                "
              >
                {{ t(tick.value === minLevel ? 'rt.common.short' : 'rt.common.long') }}
              </span>
              <span
                v-else
                class="block w-px h-3 transition-colors"
                :class="
                  isSwitchOn && currentValue === tick.value
                    ? 'bg-brand-blue1'
                    : isSwitchOn
                      ? 'bg-text-glass-default'
                      : 'bg-text-disabled'
                "
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <RtTitleValueInput
          :model-value="displayValue"
          :min="minLevel"
          :max="maxLevel"
          :disabled="loading || !isSwitchOn"
          :tooltip="hasDivergedValues ? t('rt.rapidTrigger.differentValues') : undefined"
          class="h-6"
          @commit="commitValue"
        />
        <RtRestoreButton
          size="icon-xs"
          :disabled="loading || !isSwitchOn || currentValue === defaultValue"
          @confirm="resetToDefault"
        />
      </div>

      <RtKeyDebounce :parameters="parameters" />
    </div>
  </RtCard>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Icon, Slider, SwitchLabel } from '@rapoo/element'
  import RtCard from '../RtCard.vue'
  import RtModeSelector from '../RtModeSelector.vue'
  import RtRestoreButton from '../RtRestoreButton.vue'
  import RtTitleValueInput from '../RtTitleValueInput.vue'
  import RtKeyDebounce from './RtKeyDebounce.vue'
  import type { ParametersContext } from '../../../composables/useParameters'
  import { RT_CONFIG_SECTION_META, type RtSelectionMode } from '../../types'
  import type { MouseRTValueConfig, RtSide } from '@rapoo/device-sdk'
  import RtWarningTipsButton from '../RtWarningTipsButton.vue'

  type SideMap<T> = Partial<Record<RtSide, T>>

  const { t } = useI18n()

  const props = defineProps<{
    parameters: ParametersContext
    rtSwitch?: SideMap<boolean>
    rtLevel?: SideMap<number>
    /** 当前产品 manifest 声明的 RT 级别范围与默认值。 */
    config: MouseRTValueConfig
    loading?: boolean
    mode: RtSelectionMode
  }>()

  const emit = defineEmits<{
    'update:rt-switch': [value: SideMap<boolean>]
    'update:rt-level': [value: SideMap<number>]
    'update:mode': [value: RtSelectionMode]
  }>()

  const minLevel = computed(() => props.config.min)
  const maxLevel = computed(() => props.config.max)
  /** Slider 保留 0 起点，但 RT 行程的有效配置下限仍由 manifest.min 决定。 */
  const sliderMinLevel = 0
  const stepLevel = 1
  const RT_TRAVEL_RISK_TIP = computed(() => t('rt.rapidTrigger.riskTip'))
  /** thumb 尺寸：由 Slider 同时用于视觉尺寸、range 对齐和刻度定位。 */
  const THUMB_WIDTH = 24
  const THUMB_HALF = THUMB_WIDTH / 2

  /** 按 mode 取出对应侧的 rt level 值 */
  const modeValue = computed<number | undefined>(() => {
    if (props.mode === 'left') return props.rtLevel?.left
    if (props.mode === 'right') return props.rtLevel?.right
    return props.rtLevel?.left
  })

  const defaultValue = computed(() => props.config.default)

  /**
   * 当前模式下「快速触发器」开关是否启用。
   * 关闭时本卡 slider、复位按钮和刻度点击全部禁用。
   * both 模式要求两侧开关都开启才视为启用。
   */
  const isSwitchOn = computed(() => {
    if (props.mode === 'left') return Boolean(props.rtSwitch?.left)
    if (props.mode === 'right') return Boolean(props.rtSwitch?.right)
    return Boolean(props.rtSwitch?.left) && Boolean(props.rtSwitch?.right)
  })

  /** slider 当前值：拖拽时即时更新，提交后再同步到外部 rtLevel。 */
  const sliderValue = ref<number[]>([])
  const currentValue = computed(() => sliderValue.value[0] ?? modeValue.value)

  /** 当前编辑范围内任一启用侧处于最低 RT 行程时，主动保持风险提示打开。 */
  const hasLowRtTravelRisk = computed(() => {
    const leftHasRisk = Boolean(props.rtSwitch?.left) && currentValue.value === minLevel.value
    const rightHasRisk = Boolean(props.rtSwitch?.right) && currentValue.value === minLevel.value
    if (props.mode === 'left') return leftHasRisk
    if (props.mode === 'right') return rightHasRisk
    return leftHasRisk || rightHasRisk
  })
  const isRtRiskTooltipInteractionOpen = ref(false)
  const isRtRiskTooltipOpen = computed({
    get: () => hasLowRtTravelRisk.value || isRtRiskTooltipInteractionOpen.value,
    set: (open: boolean) => {
      isRtRiskTooltipInteractionOpen.value = open
    },
  })

  /**
   * SwitchLabel 的 v-model 绑定：按当前 mode 显示/写入对应侧的开关状态。
   * - 'left' / 'right'：直接读写该侧
   * - 'both'：以两侧「都已开」作为真值；写入时同步两侧，保持开关状态一致
   */
  const switchValue = computed<boolean>({
    get: () => isSwitchOn.value,
    set: (value: boolean) => {
      if (props.mode === 'both') {
        emit('update:rt-switch', { left: value, right: value } as SideMap<boolean>)
        return
      }
      emit('update:rt-switch', { [props.mode]: value } as SideMap<boolean>)
    },
  })

  /** 按 mode 解析出要写入的一组侧 */
  function targetSides(): RtSide[] {
    return props.mode === 'both' ? ['left', 'right'] : [props.mode]
  }

  /** 复原按钮 / 滑动提交时按 mode 写入对应侧（其它侧保持不变） */
  function emitLevel(value: number) {
    const sides = targetSides()
    const payload: SideMap<number> = {}
    for (const side of sides) payload[side] = value
    emit('update:rt-level', payload)
  }

  /** 刻度标记及其百分比位置 */
  const tickMarks = computed(() => {
    const result: { value: number; percent: number }[] = []
    const range = maxLevel.value - sliderMinLevel
    for (let i = minLevel.value; i <= maxLevel.value; i++) {
      result.push({
        value: i,
        percent: range > 0 ? ((i - sliderMinLevel) / range) * 100 : 0,
      })
    }
    return result
  })

  /** 点击刻度直接跳转 */
  function jumpToTick(value: number) {
    if (!isSwitchOn.value) return
    const snapped = snap(value)
    sliderValue.value = [snapped]
    emitLevel(snapped)
  }

  watch(
    [() => props.mode, () => props.rtLevel?.left, () => props.rtLevel?.right],
    () => {
      sliderValue.value = modeValue.value === undefined ? [] : [modeValue.value]
    },
    { immediate: true },
  )

  function snap(value: number): number {
    const next = Math.round(value / stepLevel) * stepLevel
    return Math.min(maxLevel.value, Math.max(minLevel.value, next))
  }

  function onDrag(value?: number[]) {
    if (value?.[0] === undefined) return
    sliderValue.value = [snap(value[0])]
  }

  function onCommit(value: number[]) {
    if (value[0] === undefined) return
    const next = snap(value[0])
    sliderValue.value = [next]
    emitLevel(next)
  }

  /** 复原按钮：把当前 mode 对应侧的 rt level 恢复到 manifest 初始值 */
  function resetToDefault() {
    if (!isSwitchOn.value) return
    const target = snap(defaultValue.value)
    sliderValue.value = [target]
    emitLevel(target)
  }

  /** 标题输入框展示值：关闭快速触发器时以 `\` 表示当前值不可用。 */
  const leftLevel = computed(() => props.rtLevel?.left)
  const rightLevel = computed(() => props.rtLevel?.right)

  const hasDivergedValues = computed(
    () =>
      props.mode === 'both' &&
      leftLevel.value !== undefined &&
      rightLevel.value !== undefined &&
      leftLevel.value !== rightLevel.value,
  )

  const displayValue = computed(() => {
    if (!isSwitchOn.value) return '\\'
    if (props.mode === 'left') return leftLevel.value === undefined ? '' : String(leftLevel.value)
    if (props.mode === 'right')
      return rightLevel.value === undefined ? '' : String(rightLevel.value)
    if (hasDivergedValues.value) return '/'
    if (leftLevel.value !== undefined) return String(leftLevel.value)
    if (rightLevel.value !== undefined) return String(rightLevel.value)
    return ''
  })

  /** 手输值由共享输入框完成校验和夹取；both 模式会同步写入两侧。 */
  function commitValue(value: number) {
    emitLevel(value)
  }
</script>
